import { useSubscription } from '@apollo/client'
import { Flex } from '@chakra-ui/react'
import { DateTime } from 'luxon'

import { isNotNullOrUndefined, joinWithConjunction } from '@/utils/helpers'
import type { UserPresenceKey } from '@/server/services/userPresence/types'

import { useUser } from '@/client/context/user'
import useForceUpdate from '@/client/hooks/useForceUpdate'
import useInterval from '@/client/hooks/useInterval'
import type {
  ConversationTypingStatusConversationFragment,
  ConversationTypingStatusMessageFragment,
  ConversationTypingStatusUserPresenceSubscription,
  ConversationTypingStatusUserPresenceSubscriptionVariables,
} from '@/client/services/hasura/generated/graphql'

import { SUBSCRIPTION_CONVERSATION_TYPING_STATUS_USER_PRESENCE } from './ConversationTypingStatus.hasura'

const OPTIMISTIC_TYPING_THRESHOLD_SECONDS = 5

export default function ConversationTypingStatus({
  conversation,
  messages,
}: {
  conversation: ConversationTypingStatusConversationFragment
  messages: ConversationTypingStatusMessageFragment[]
}) {
  const user = useUser()
  const forceUpdate = useForceUpdate()

  useInterval(forceUpdate, (OPTIMISTIC_TYPING_THRESHOLD_SECONDS / 2) * 1000, [])

  const key: UserPresenceKey = `conversation:${conversation.id}`
  const { data } = useSubscription<
    ConversationTypingStatusUserPresenceSubscription,
    ConversationTypingStatusUserPresenceSubscriptionVariables
  >(SUBSCRIPTION_CONVERSATION_TYPING_STATUS_USER_PRESENCE, {
    skip: conversation?.id == null,
    variables: {
      key,
    },
  })

  const presentUserIds = (data?.user_presence?.map(p => p.user_id) ?? []).filter(
    item => item !== user.id
  )

  // Only look at the last 10 messages
  const messagesToLookAt = messages.slice(-10)

  const userNames = presentUserIds
    // Filter if they sent a message within the last few seconds or so
    .filter(
      presentUserId =>
        !messagesToLookAt.some(
          message =>
            message.user_id === presentUserId &&
            Math.abs(DateTime.fromISO(message.created_at).diffNow().as('seconds')) <
              OPTIMISTIC_TYPING_THRESHOLD_SECONDS
        )
    )
    // Map to conversation_user names
    .map(
      presentUserId =>
        conversation.conversation_users.find(cu => cu.user_id === presentUserId)?.user
          ?.virtual_display_name
    )
    .filter(isNotNullOrUndefined)

  if (userNames.length === 0) {
    return null
  }

  return (
    <Flex flexDir="row" justifyContent="left" p="8px" color="fg.subtle">
      {joinWithConjunction(userNames, 'and')} {userNames.length === 1 ? 'is' : 'are'} typing!
    </Flex>
  )
}
